import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Lock, Trash2 } from "lucide-react";
import {
  describeDeviceCapabilities,
  getRemoteLockErrorMessage,
  getRemoteLockInventory,
  getRemoteLockMappings,
  getRemoteLockStatus,
  isRemoteLockDisabledError,
  removeRemoteLockMapping,
  saveRemoteLockMapping,
  selectableInventoryDevices,
} from "../../utils/remotelock";

/**
 * "Smart lock" block inside Guest Access on the property page. The host picks
 * the property's primary entry point (and optional extra doors such as a gate
 * or door group) from the devices discovered in their RemoteLock account.
 *
 * Only ids from the stored inventory are sent; the backend re-validates them.
 */
export default function RemoteLockDoorMapping({ hostId, propertyId }) {
  const [state, setState] = useState({ loading: true, hidden: false, connected: false });
  const [devices, setDevices] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'ok'|'error'|'confirm', text }

  const load = useCallback(async () => {
    if (!hostId || !propertyId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const status = await getRemoteLockStatus(hostId);
      if (!status?.connected) {
        setState({ loading: false, hidden: false, connected: false, status: status?.status });
        return;
      }
      const [inventory, mappings] = await Promise.all([getRemoteLockInventory(hostId), getRemoteLockMappings(hostId)]);
      const selectable = selectableInventoryDevices(inventory?.devices || []);
      setDevices(selectable);
      const mine = (mappings?.mappings || []).find((m) => m.propertyId === String(propertyId) && m.status === "active") || null;
      setMapping(mine);
      setPrimary(mine?.primaryAccessible?.id || "");
      setSecondary((mine?.secondaryAccessibles || []).map((a) => a.id));
      setState({ loading: false, hidden: false, connected: true, truncated: Boolean(inventory?.truncated) });
    } catch (error) {
      if (isRemoteLockDisabledError(error)) {
        setState({ loading: false, hidden: true, connected: false });
        return;
      }
      setState({ loading: false, hidden: false, connected: false, error: getRemoteLockErrorMessage(error, "Failed to load smart lock settings") });
    }
  }, [hostId, propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const byId = useMemo(() => Object.fromEntries(devices.map((d) => [d.id, d])), [devices]);
  const dirty = primary !== (mapping?.primaryAccessible?.id || "") || secondary.join(",") !== (mapping?.secondaryAccessibles || []).map((a) => a.id).join(",");

  const save = async ({ multiPropertyConfirmed = false } = {}) => {
    if (!primary) return;
    setSaving(true);
    setMessage(null);
    try {
      const out = await saveRemoteLockMapping(hostId, propertyId, {
        primaryAccessibleId: primary,
        secondaryAccessibleIds: secondary.filter((id) => id !== primary),
        multiPropertyConfirmed,
      });
      setMessage({ type: "ok", text: `Saved. Deliveries to this property will use "${out?.mapping?.primaryAccessible?.name || byId[primary]?.name || "the selected door"}".` });
      await load();
    } catch (error) {
      if (error?.response?.status === 409 && error?.response?.data?.code === "door_shared") {
        setMessage({ type: "confirm", text: error.response.data.message });
      } else {
        setMessage({ type: "error", text: getRemoteLockErrorMessage(error, "Failed to save door mapping") });
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the smart lock from this property? Upcoming deliveries will fall back to written entry instructions.")) return;
    setSaving(true);
    setMessage(null);
    try {
      await removeRemoteLockMapping(hostId, propertyId);
      setMessage({ type: "ok", text: "Smart lock removed from this property." });
      await load();
    } catch (error) {
      setMessage({ type: "error", text: getRemoteLockErrorMessage(error, "Failed to remove door mapping") });
    } finally {
      setSaving(false);
    }
  };

  const toggleSecondary = (id) => setSecondary((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 5 ? cur : [...cur, id]));

  if (state.hidden) return null;

  return (
    <div className="mt-5 rounded-xl border border-[#EAEAEA] bg-[#F7F7F8] p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lock size={14} className="text-gray-500" />
          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-900">Smart lock</h3>
        </div>
        {state.connected && mapping && (
          <span className="rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-bold text-green-700">Mapped</span>
        )}
      </div>

      {state.loading ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <Loader2 size={12} className="animate-spin" /> Loading lock settings…
        </p>
      ) : !state.connected ? (
        <p className="mt-3 text-xs text-gray-500">
          {state.error ? (
            state.error
          ) : (
            <>
              Connect RemoteLock to issue temporary door codes for each delivery.{" "}
              <Link to="/integrations" className="font-semibold text-gray-900 hover:underline">
                Open Integrations
              </Link>
            </>
          )}
        </p>
      ) : devices.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500">
          No doors found in your RemoteLock account yet. Use <span className="font-semibold">Refresh locks</span> on the Integrations page after your locks are online.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {state.truncated && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">Device list is incomplete. Contact support before mapping.</p>
          )}
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Primary entry door</label>
            <select
              value={primary}
              onChange={(e) => {
                setPrimary(e.target.value);
                setMessage(null);
              }}
              disabled={saving}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-400"
            >
              <option value="">Select a door…</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.id}
                  {d.locationName ? ` — ${d.locationName}` : ""}
                  {` (${describeDeviceCapabilities(d)})`}
                </option>
              ))}
            </select>
            {primary && byId[primary] && (
              <p className="mt-1 text-[11px] text-gray-500">
                {byId[primary].supportsRemoteUnlock === true
                  ? "Concierges can unlock this door from the app during the delivery window, and receive a one-time code as backup."
                  : byId[primary].supportsRemoteUnlock === false
                  ? "This door can't be unlocked remotely. Concierges will receive a one-time code for the delivery window."
                  : "We couldn't confirm this door's capabilities. A one-time code will be issued; remote unlock will be offered only if the lock reports support."}
              </p>
            )}
          </div>

          {devices.length > 1 && (
            <div>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Additional doors (gate, lobby, door group)</p>
              <div className="flex flex-wrap gap-1.5">
                {devices
                  .filter((d) => d.id !== primary)
                  .map((d) => {
                    const on = secondary.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        disabled={saving}
                        onClick={() => {
                          toggleSecondary(d.id);
                          setMessage(null);
                        }}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                          on ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        {on && <Check size={11} />}
                        {d.name || d.id}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {message && (
            <div
              className={`rounded-lg px-3 py-2 text-xs ${
                message.type === "ok"
                  ? "border border-green-200 bg-green-50 text-green-800"
                  : message.type === "confirm"
                  ? "border border-amber-200 bg-amber-50 text-amber-800"
                  : "border border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {message.text}
              {message.type === "confirm" && (
                <button type="button" onClick={() => save({ multiPropertyConfirmed: true })} disabled={saving} className="ml-2 font-bold underline">
                  Share this door anyway
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => save()}
              disabled={saving || !primary || !dirty}
              className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-xs font-bold text-white transition hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              {mapping ? "Update mapping" : "Save mapping"}
            </button>
            {mapping && (
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
              >
                <Trash2 size={12} />
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
